import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

const STORAGE_KEY = 'cellsnake:dailyWinner';

/**
 * Persisted record of the day's current leader. Reset every UTC midnight.
 * In a real backend this would be a server query; for now we shadow it
 * in localStorage and only update from the local leaderboard, which is
 * enough to demo the loop.
 */
interface WinnerRecord {
  /** ISO date `YYYY-MM-DD` — the reset key. */
  dateKey: string;
  /** Display name of the current daily leader. */
  name: string;
  /** Top mass that name achieved today. */
  mass: number;
  /** Hue of the leader for colored display. */
  hue: number;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function loadWinner(): WinnerRecord {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const w = JSON.parse(raw) as WinnerRecord;
      if (w.dateKey === todayKey()) return w;
    }
  } catch { /* private mode */ }
  return { dateKey: todayKey(), name: '—', mass: 0, hue: 200 };
}

function saveWinner(w: WinnerRecord): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(w)); } catch { /* noop */ }
}

/** Format seconds-remaining into HH:MM:SS for the countdown display. */
function formatHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Daily winner clock — two-line dock pill rendered at the top of the
 * landing AND the in-game HUD. Structured per design:
 *
 *   NEXT DAILY WINNER IN
 *   06:42:18                       ← bigger, mono, pulses under 1h
 *   DAILY CHAMPION ● ECHO · 8,420   ← OR "no champion yet"
 *
 * Tick rate: 1 Hz. Updates the countdown + checks for a new champion
 * by scanning the live leaderboard's top row. At UTC midnight the
 * record resets to "no champion yet" and a fresh cycle starts.
 *
 * Backend-ready: when we wire a real cross-session leaderboard, swap
 * `loadWinner`/`saveWinner` for an API call — the UI doesn't change.
 *
 * `variant` controls the visual treatment:
 *   - 'hud':     compact pill, sits over the game canvas at top-center.
 *   - 'landing': slightly larger pill, designed to read as a header
 *                element on the landing page.
 */
export function DailyWinnerClock({ variant = 'hud' }: { variant?: 'hud' | 'landing' } = {}) {
  const leaderboard = useGameStore((s) => s.leaderboard);
  const theme = useGameStore((s) => s.theme);
  const isLight = theme === 'light';
  const [winner, setWinner] = useState<WinnerRecord>(loadWinner);
  const [remaining, setRemaining] = useState<number>(0);

  // Recompute countdown every second + check for new daily leader.
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setUTCHours(24, 0, 0, 0); // next UTC midnight
      setRemaining(Math.max(0, Math.floor((midnight.getTime() - now.getTime()) / 1000)));

      // Roll over if the date changed.
      const today = todayKey();
      if (winner.dateKey !== today) {
        const fresh: WinnerRecord = { dateKey: today, name: '—', mass: 0, hue: 200 };
        setWinner(fresh);
        saveWinner(fresh);
      }
    };
    tick();
    const h = window.setInterval(tick, 1000);
    return () => clearInterval(h);
  }, [winner.dateKey]);

  // Watch the leaderboard for a new top mass.
  useEffect(() => {
    const top = leaderboard[0];
    if (!top) return;
    if (top.mass > winner.mass) {
      const next: WinnerRecord = {
        dateKey: todayKey(),
        name: top.name,
        mass: Math.round(top.mass),
        hue: top.hue,
      };
      setWinner(next);
      saveWinner(next);
    }
  }, [leaderboard, winner.mass]);

  const color = `hsl(${winner.hue} 100% 70%)`;
  const hasChampion = winner.mass > 0 && winner.name !== '—';
  const isLanding = variant === 'landing';

  // Last hour urgency: amber digits + subtle pulse so the player FEELS
  // the daily window closing. Switches back to brand cyan at midnight
  // when the fresh cycle begins. On light mode the neon colors wash
  // out against the off-white background, so we drop the saturation
  // and brightness to a deeper teal/burnt-amber that retains the hue
  // family while staying legible.
  const urgentLastHour = remaining > 0 && remaining < 3600;
  const countdownColor = isLight
    ? (urgentLastHour ? '#a05900' : '#0b6e8a')
    : (urgentLastHour ? '#ffd23d' : '#22e6ff');

  // Entry animation: only the landing variant gets the staggered drop-
  // in (it's part of the "land in space" choreography). The HUD variant
  // mounts when the user starts a game — they're already inside the
  // experience, no entry animation needed there.
  const enterProps = isLanding
    ? {
        initial: { opacity: 0, y: -14, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: { delay: 1.1, duration: 0.45, ease: [0.2, 0.7, 0.3, 1] as const },
      }
    : {
        initial: false as const,
      };
  return (
    <motion.div
      className={`absolute ${isLanding ? 'top-4' : 'top-3'} left-1/2 -translate-x-1/2 z-10 pointer-events-none ${isLanding ? '' : 'hidden md:block'}`}
      role="status"
      aria-live="polite"
      aria-label={
        hasChampion
          ? `Next daily winner in ${formatHMS(remaining)}. Current daily champion: ${winner.name} with ${winner.mass} mass.`
          : `Next daily winner in ${formatHMS(remaining)}. No champion yet today.`
      }
      {...enterProps}
    >
      <div
        className={`nx-dock-pill flex flex-col items-center rounded-2xl ${
          isLanding ? 'px-5 py-2.5 min-w-[260px]' : 'px-4 py-2 min-w-[230px]'
        }`}
      >
        {/* Row 1 — countdown */}
        <div className="flex items-baseline gap-2">
          <span className={`uppercase tracking-[0.28em] text-white/55 ${isLanding ? 'text-[9px]' : 'text-[8px]'}`}>
            next daily winner in
          </span>
          <span
            className={`font-mono tabular-nums font-semibold ${
              isLanding ? 'text-base' : 'text-[13px]'
            } ${urgentLastHour ? 'animate-pulse' : ''}`}
            style={{
              color: countdownColor,
              textShadow: `0 0 8px ${countdownColor}55`,
            }}
          >
            {formatHMS(remaining)}
          </span>
        </div>

        {/* Row 2 — champion (or empty state) */}
        <div className={`flex items-center gap-1.5 mt-0.5 ${isLanding ? 'text-[10px]' : 'text-[9px]'} uppercase tracking-[0.22em]`}>
          <span className="text-white/45">daily champion</span>
          <span className="text-white/25">·</span>
          {hasChampion ? (
            <>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: color, boxShadow: `0 0 5px ${color}` }}
                aria-hidden
              />
              <span style={{ color }} className="font-semibold">
                {winner.name}
              </span>
              <span className="text-white/30">·</span>
              <span className="text-white/85 tabular-nums">
                {winner.mass.toLocaleString()}
              </span>
            </>
          ) : (
            <span className="text-white/45 italic normal-case tracking-normal">
              no champion yet
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
